import React from "react";
import PropTypes from "prop-types";
import { generatePath, useHistory } from "react-router-dom";
import { Avatar, Tooltip } from "@mui/material";
import dayjs from "dayjs";
import "dayjs/locale/ar";

import {
  UITable,
  UIStatusTag,
  UIRowActionsMenu,
  getUserInitials,
} from "../../../../../components/ui";
import TruncatedWithTooltip from "../../../../../components/TruncatedWithTooltip";
import { getCompanyStatusKey } from "./companiesListRowHelpers";
import { ROUTE_PATHS } from "../../../../../utils/RoutesPaths";
import {
  COMPANY_DETAILS_OVERVIEW_TAB,
  buildCompanyDetailsTabSearch,
} from "../../../TeamManagement/adminCompanyTeamRoutes";

import "./CompaniesTable.scss";

const EMPTY_VALUE = "-";

const rowCompanyId = (row) => {
  const code = row.company_code ?? row.reference_code ?? row.slug;
  if (code != null && String(code).trim()) return `#${String(code).trim()}`;
  return `#C${row.id}`;
};

const rowContact = (row, lang) =>
  row.company_owner?.name ||
  row.company_owner?.[`full_name_${lang}`] ||
  row.contact_person_name ||
  row.contact_name ||
  EMPTY_VALUE;

const rowCity = (row, lang) =>
  row.city?.[`name_${lang}`] ||
  row.city?.name_en ||
  row.city_name ||
  EMPTY_VALUE;

const rowEvents = (row) => {
  const count = row.number_of_published_events;
  if (count == null || count === "") return 0;
  const parsed = Number(count);
  return Number.isFinite(parsed) ? parsed : 0;
};

const companyNameRowPropType = PropTypes.shape({
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  name_en: PropTypes.string,
  name_ar: PropTypes.string,
  image_url: PropTypes.string,
  status: PropTypes.string,
  is_active: PropTypes.bool,
  active: PropTypes.bool,
  company_owner: PropTypes.shape({
    name: PropTypes.string,
    full_name_en: PropTypes.string,
    full_name_ar: PropTypes.string,
  }),
}).isRequired;

const CompanyNameCell = ({ row, lang }) => {
  const nameEn = row.name_en?.trim() || EMPTY_VALUE;
  const nameAr = row.name_ar?.trim() || EMPTY_VALUE;
  const displayName = nameEn !== EMPTY_VALUE ? nameEn : nameAr;
  const tooltipTitle = (
    <>
      <div>{nameEn}</div>
      <div className="companies-table-company-cell__tooltip-subtext">
        {nameAr}
      </div>
    </>
  );

  return (
    <div
      className="companies-table-company-cell"
      style={{ direction: lang === "ar" ? "rtl" : "ltr" }}
    >
      <Avatar
        src={row.image_url || undefined}
        alt={displayName}
        className="companies-table-company-cell__avatar"
      >
        {getUserInitials(displayName)}
      </Avatar>
      <Tooltip
        title={tooltipTitle}
        placement="top"
        arrow
        disableHoverListener={nameEn === EMPTY_VALUE && nameAr === EMPTY_VALUE}
      >
        <div className="companies-table-company-cell__text">
          <span className="MuiDataGrid-cellContent companies-table-company-cell__primary">
            {nameEn}
          </span>
          <span
            className="MuiDataGrid-cellContent companies-table-company-cell__subtext"
            dir={lang === "ar" ? "rtl" : "ltr"}
          >
            {nameAr}
          </span>
        </div>
      </Tooltip>
    </div>
  );
};

CompanyNameCell.propTypes = {
  row: companyNameRowPropType,
  lang: PropTypes.string.isRequired,
};

const renderCellContent = (value) => (
  <TruncatedWithTooltip title={value}>
    <span className="MuiDataGrid-cellContent">{value}</span>
  </TruncatedWithTooltip>
);

const CompaniesTable = ({
  rows,
  lang,
  labels,
  shared,
  emptyState,
  onDeactivate,
  onActivate,
}) => {
  const history = useHistory();

  // Parse only the date portion (YYYY-MM-DD) to avoid timezone conversion
  // shifting the day when the timestamp is near midnight in a UTC+3 offset.
  const formatCreated = (iso) =>
    dayjs(iso.substring(0, 10))
      .locale(lang === "ar" ? "ar" : "en")
      .format("DD MMM YYYY");

  const viewCompanyDetails = (row) => {
    history.push({
      pathname: generatePath(ROUTE_PATHS.companiesListCompanyDetails, {
        id: row.id,
      }),
      search: buildCompanyDetailsTabSearch(COMPANY_DETAILS_OVERVIEW_TAB),
    });
  };

  const companyStatusLabels = {
    pending: labels.pending,
    rejected: labels.rejected,
    deactivated: labels.deactivated,
    active: labels.active,
  };

  const getCompanyRowActionItems = (row) => {
    const items = [
      {
        key: "edit",
        label: shared.edit,
        onClick: () =>
          history.push(
            generatePath(ROUTE_PATHS.companiesListCompanyEdit, {
              id: row.id,
            }),
          ),
      },
    ];

    const statusKey = getCompanyStatusKey(row);
    if (statusKey === "active") {
      items.push({
        key: "deactivate",
        label: labels.deactivation.menuAction,
        danger: true,
        onClick: () => onDeactivate(row),
      });
    }
    if (statusKey === "deactivated") {
      items.push({
        key: "activate",
        label: labels.activation.menuAction,
        success: true,
        onClick: () => onActivate(row),
      });
    }

    return items;
  };

  const columns = [
    {
      field: "companyId",
      headerName: labels.companyId,
      flex: 0.65,
      minWidth: 96,
      sortable: false,
      valueGetter: (params) => rowCompanyId(params.row),
      renderCell: (params) => renderCellContent(params.value),
    },
    {
      field: "company",
      headerName: labels.companyName,
      flex: 1.35,
      minWidth: 220,
      sortable: false,
      renderCell: (params) => <CompanyNameCell row={params.row} lang={lang} />,
    },
    {
      field: "contactPerson",
      headerName: labels.contactPerson,
      flex: 0.85,
      minWidth: 128,
      sortable: false,
      valueGetter: (params) => rowContact(params.row, lang),
      renderCell: (params) => renderCellContent(params.value),
    },
    {
      field: "city",
      headerName: labels.city,
      flex: 0.75,
      minWidth: 112,
      sortable: false,
      valueGetter: (params) => rowCity(params.row, lang),
      renderCell: (params) => renderCellContent(params.value),
    },
    {
      field: "created_at",
      headerName: labels.createdDate,
      flex: 0.8,
      // Fits "30 سبتمبر 2026" plus the cell's 30px side padding — never cut.
      minWidth: 170,
      sortable: false,
      renderCell: (params) =>
        renderCellContent(
          params.row.created_at
            ? formatCreated(params.row.created_at)
            : EMPTY_VALUE
        ),
    },
    {
      field: "events",
      headerName: labels.numberOfEvents,
      flex: 0.65,
      minWidth: 104,
      sortable: false,
      align: "center",
      headerAlign: "center",
      valueGetter: (params) => rowEvents(params.row),
      renderCell: (params) => renderCellContent(params.value ?? 0),
    },
    {
      field: "status",
      headerName: labels.status,
      flex: 0.75,
      minWidth: 112,
      sortable: false,
      renderCell: (params) => {
        const statusKey = getCompanyStatusKey(params.row);

        return (
          <UIStatusTag
            preset="table"
            status={statusKey}
            label={companyStatusLabels[statusKey] ?? labels.active}
          />
        );
      },
    },
    {
      field: "actions",
      headerName: shared.actions,
      flex: 0.55,
      minWidth: 84,
      sortable: false,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <UIRowActionsMenu
          ariaLabel={shared.actions}
          items={getCompanyRowActionItems(params.row)}
        />
      ),
    },
  ];

  if (rows.length === 0 && emptyState) {
    return <div className="companies-list-table__empty">{emptyState}</div>;
  }

  return (
    <UITable
      rows={rows}
      columns={columns}
      contentSized
      hideFooter
      className="companies-list-table"
      onRowClick={(params) => viewCompanyDetails(params.row)}
    />
  );
};

CompaniesTable.propTypes = {
  rows: PropTypes.arrayOf(companyNameRowPropType),
  lang: PropTypes.string.isRequired,
  labels: PropTypes.object.isRequired,
  shared: PropTypes.object.isRequired,
  emptyState: PropTypes.node,
  /** Opens the deactivation modal for an Active company's row. */
  onDeactivate: PropTypes.func.isRequired,
  /** Opens the activation modal for a deactivated company's row. */
  onActivate: PropTypes.func.isRequired,
};

CompaniesTable.defaultProps = {
  rows: [],
  emptyState: null,
};

export default CompaniesTable;
